import React, { useEffect } from 'react';

/**
 * Reusable Modal/Popup component that works on all networks (HTTP, HTTPS, etc.)
 * Features:
 * - Backdrop click handling
 * - Escape key support
 * - Fixed z-index to prevent overlapping
 * - Accessible keyboard interactions
 */
function Modal({
  isOpen = false,
  onClose = () => {},
  title = '',
  children,
  maxWidth = '450px',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = ''
}) {
  // Handle escape key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px',
          boxSizing: 'border-box',
          zIndex: 9998,
          animation: 'fadeIn 0.3s ease-in-out'
        }}
        onClick={handleBackdropClick}
        role="presentation"
      >
        {/* Modal Content */}
        <div
          style={{
            backgroundColor: '#2a2d35',
            borderRadius: '12px',
            padding: 'clamp(16px, 4vw, 30px)',
            maxWidth: maxWidth,
            width: '90%',
            boxSizing: 'border-box',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid #444',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            zIndex: 9999,
            animation: 'slideUp 0.3s ease-in-out'
          }}
          onClick={(e) => e.stopPropagation()}
          className={className}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {title && (
            <h3
              id="modal-title"
              style={{
                margin: 0,
                marginBottom: 'clamp(16px, 3vw, 30px)',
                fontSize: 'clamp(18px, 4vw, 24px)',
                fontWeight: 'bold',
                color: '#fff',
                textAlign: 'center'
              }}
            >
              {title}
            </h3>
          )}
          {children}
        </div>
      </div>

      {/* Add animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default Modal;
